import { axiosAuth } from 'Utils/axios'
import { AppResp } from 'Types/api'
import { BASE_API_URL } from '@/constant/api'
import { Message } from 'Types/message'
import {
  BacktrackReq,
  ChatHistoryReq,
  ContinueReplyReq,
  ContinueReplyRes,
  EditChatReplyReq,
  EditStoryReplyReq,
  GenInspirationReq,
  GenInspirationRes,
  RetellReq,
  RetellRes,
  SendAnotherMsgReq,
  SendAnotherMsgRes,
  SendMessageReq,
  SendMessageRes,
  StartFromAMessageReq,
} from 'Types/chat'
import {
  ChattedCharacterListRes,
  DeleteUserChattedCharacterReq,
  SaveAndRestartConversation,
  SaveAndRestartConversationCreateRes,
  UserChattedListReq,
  UserChattedListRes,
} from '@/types/character'
import { XhrSource } from 'Utils/sse'
import { getAuthToken, hasJwtNotExpired } from '@/lib/auth/jwt'

const getStreamAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Platform: 'web',
  }
  const token = getAuthToken()
  if (token && hasJwtNotExpired(token)) {
    headers.token = token
  }
  return headers
}

export const chatHistory = async (
  character_id: string,
  startSequence: number | 'max',
  size: number,
  language_code: string
) => {
  let sequence
  if (startSequence === 'max') {
    sequence = '0'
  } else {
    sequence = String(startSequence)
  }
  const req: ChatHistoryReq = {
    character_id,
    sequence,
    language_code,
    size,
  }
  const res = await axiosAuth.post<AppResp<Message[]>>(
    `${BASE_API_URL}/chat/history`,
    req
  )
  return res.data.data
}

export const storyHistory = async (
  character_id: string,
  startSequence: number | 'max',
  size: number,
  language_code: string
) => {
  let sequence
  if (startSequence === 'max') {
    sequence = '0'
  } else {
    sequence = String(startSequence)
  }

  const req = {
    story_id: character_id,
    sequence,
    size,
    language_code,
  }

  const res = await axiosAuth.post<AppResp<Message[]>>(
    `${BASE_API_URL}/chat/story/history`,
    req
  )

  if (!res.data.data) {
    throw new Error('No chat history found')
  }
  return res.data.data
}

export const sendMessage = async (
  character_id: string,
  content: string,
  edit_reply?: EditChatReplyReq
) => {
  const req: SendMessageReq = {
    character_id,
    content,
    edit_reply,
  }
  const res = await axiosAuth.post<AppResp<SendMessageRes>>(
    `${BASE_API_URL}/chat/send_msg`,
    req
  )
  return res.data.data
}

export const sendStoryMessage = async (
  character_id: string,
  content: string
) => {
  const req = {
    story_id: character_id,
    content,
  }
  const res = await axiosAuth.post<AppResp<SendMessageRes>>(
    `${BASE_API_URL}/chat/story/send_msg`,
    req
  )

  if (!res.data.data) {
    throw new Error('handleSendInspiration error: empty response')
  }

  return res.data.data
}

export const sendMessageStream = async (
  character_id: string,
  content: string,
  onData: (data: string) => void,
  edit_reply?: EditChatReplyReq
): Promise<SendMessageRes> => {
  const req = {
    character_id,
    content,
    edit_reply,
  }

  const eventSource = XhrSource(`${BASE_API_URL}/chat/send_msg_stream`, {
    method: 'POST',
    withCredentials: true,
    headers: getStreamAuthHeaders(),
    body: JSON.stringify(req),
  })

  return new Promise((resolve, reject) => {
    eventSource.addEventListener('message', (event: Event) => {
      const messageEvent = event as MessageEvent
      if (messageEvent.data) {
        onData(messageEvent.data)
      }
    })

    eventSource.addEventListener('complete', (event: Event) => {
      const messageEvent = event as MessageEvent
      try {
        const resp: AppResp<SendMessageRes> = JSON.parse(messageEvent.data)
        if (resp.code !== 0 || !resp.data) {
          reject(new Error(resp.msg || 'Unknown error'))
          return
        }
        resolve(resp.data)
      } catch (e) {
        reject(new Error('Invalid response'))
      }
    })

    eventSource.addEventListener('error', (error: Event) => {
      const closeEvent = error as CloseEvent
      reject(new Error(closeEvent?.reason || 'Unknown error'))
    })
  })
}

export const backtrack = async (
  character_id: string,
  sequence: string,
  conversation_id?: string
) => {
  const req: BacktrackReq = {
    character_id,
    sequence,
    ...(conversation_id ? { conversation_id } : {}),
  }
  return axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/chat/backtrack`,
    req
  )
}

export const backtrackStory = async (
  character_id: string,
  sequence: string
) => {
  const req = {
    story_id: character_id,
    sequence,
  }
  return axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/chat/story/backtrack`,
    req
  )
}

export const reset = async (character_id: string) => {
  const res = await axiosAuth.post<
    AppResp<{
      new_conversation_id: string
      new_conversation_name: string
      archived_conversation_id: string
      new_instance_id: string
    }>
  >(`${BASE_API_URL}/chat/reset_oc`, {
    character_id,
  })
  return res.data.data
}

export const resetStory = async (character_id: string) => {
  return backtrackStory(character_id, '0')
}

export const retell = async (character_id: string, sequence: string) => {
  const req: RetellReq = {
    character_id,
    sequence,
  }
  const res = await axiosAuth.post<AppResp<RetellRes>>(
    `${BASE_API_URL}/chat/retell`,
    req
  )
  return res.data.data
}

export const retellStory = async (character_id: string, sequence: string) => {
  const req = {
    story_id: character_id,
    sequence,
  }
  const res = await axiosAuth.post<AppResp<RetellRes>>(
    `${BASE_API_URL}/chat/story/retell`,
    req
  )

  if (!res.data.data) {
    throw new Error('handleRetell error: empty response')
  }

  return res.data.data
}

export const retellStream = async (
  character_id: string,
  sequence: string,
  onData: (data: string) => void
): Promise<RetellRes> => {
  const req = {
    character_id,
    sequence,
  }

  const eventSource = XhrSource(`${BASE_API_URL}/chat/retell_stream`, {
    method: 'POST',
    withCredentials: true,
    headers: getStreamAuthHeaders(),
    body: JSON.stringify(req),
  })

  return new Promise((resolve, reject) => {
    eventSource.addEventListener('message', (event: Event) => {
      const messageEvent = event as MessageEvent
      if (messageEvent.data) {
        onData(messageEvent.data)
      }
    })

    eventSource.addEventListener('complete', (event: Event) => {
      const messageEvent = event as MessageEvent
      try {
        const resp: AppResp<RetellRes> = JSON.parse(messageEvent.data)
        if (resp.code !== 0 || !resp.data) {
          reject(new Error(resp.msg || 'Unknown error'))
          return
        }
        resolve(resp.data)
      } catch (e) {
        reject(new Error('Invalid response'))
      }
    })

    eventSource.addEventListener('error', (error: Event) => {
      const closeEvent = error as CloseEvent
      reject(new Error(closeEvent?.reason || 'Unknown error'))
    })
  })
}

export const sendAnotherMsg = async (
  character_id: string,
  edit_reply?: EditChatReplyReq
) => {
  const req: SendAnotherMsgReq = {
    character_id,
    edit_reply,
  }
  const res = await axiosAuth.post<AppResp<SendAnotherMsgRes>>(
    `${BASE_API_URL}/chat/reply_msg`,
    req
  )
  return res.data.data
}

export const sendAnotherMsgStory = async (character_id: string) => {
  const req = {
    story_id: character_id,
  }
  const res = await axiosAuth.post<AppResp<SendAnotherMsgRes>>(
    `${BASE_API_URL}/chat/story/reply_msg`,
    req
  )

  if (!res.data.data) throw new Error('Send another message failed')

  return res.data.data
}

export const sendAnotherMsgStream = async (
  character_id: string,
  onData: (data: string) => void,
  edit_reply?: EditChatReplyReq
): Promise<SendAnotherMsgRes> => {
  const req = {
    character_id,
    edit_reply,
  }

  const eventSource = XhrSource(`${BASE_API_URL}/chat/reply_msg_stream`, {
    method: 'POST',
    withCredentials: true,
    headers: getStreamAuthHeaders(),
    body: JSON.stringify(req),
  })

  return new Promise((resolve, reject) => {
    eventSource.addEventListener('message', (event: Event) => {
      const messageEvent = event as MessageEvent
      if (messageEvent.data) {
        onData(messageEvent.data)
      }
    })

    eventSource.addEventListener('complete', (event: Event) => {
      const messageEvent = event as MessageEvent
      try {
        const resp: AppResp<SendAnotherMsgRes> = JSON.parse(messageEvent.data)
        if (resp.code !== 0 || !resp.data) {
          reject(new Error(resp.msg || 'Unknown error'))
          return
        }
        resolve(resp.data)
      } catch (e) {
        reject(new Error('Invalid response'))
      }
    })

    eventSource.addEventListener('error', (error: Event) => {
      const closeEvent = error as CloseEvent
      reject(new Error(closeEvent?.reason || 'Unknown error'))
    })
  })
}

export const continueReply = async (
  character_id: string,
  edit_reply?: EditChatReplyReq
) => {
  const req: ContinueReplyReq = {
    character_id,
    edit_reply,
  }
  const res = await axiosAuth.post<AppResp<ContinueReplyRes>>(
    `${BASE_API_URL}/chat/continue_reply`,
    req
  )
  return res.data.data
}

export const continueReplyStream = async (
  character_id: string,
  onData: (data: string) => void,
  edit_reply?: EditChatReplyReq
): Promise<ContinueReplyRes> => {
  const req = {
    character_id,
    edit_reply,
  }

  const eventSource = XhrSource(`${BASE_API_URL}/chat/continue_reply_stream`, {
    method: 'POST',
    withCredentials: true,
    headers: getStreamAuthHeaders(),
    body: JSON.stringify(req),
  })

  return new Promise((resolve, reject) => {
    eventSource.addEventListener('message', (event: Event) => {
      const messageEvent = event as MessageEvent
      if (messageEvent.data) {
        onData(messageEvent.data)
      }
    })

    eventSource.addEventListener('complete', (event: Event) => {
      const messageEvent = event as MessageEvent
      try {
        const resp: AppResp<ContinueReplyRes> = JSON.parse(messageEvent.data)
        if (resp.code !== 0 || !resp.data) {
          reject(new Error(resp.msg || 'Unknown error'))
          return
        }
        resolve(resp.data)
      } catch (e) {
        reject(new Error('Invalid response'))
      }
    })

    eventSource.addEventListener('error', (error: Event) => {
      const closeEvent = error as CloseEvent
      reject(new Error(closeEvent?.reason || 'Unknown error'))
    })
  })
}

export const genInspiration = async (
  character_id: string,
  edit_reply: EditChatReplyReq | undefined,
  size: number,
  nsfw: boolean,
  language_code: string,
  is_refresh: boolean = false
) => {
  const req: GenInspirationReq = {
    character_id,
    edit_reply,
    size,
    nsfw,
    language_code,
    is_refresh,
  }
  const res = await axiosAuth.post<AppResp<GenInspirationRes>>(
    `${BASE_API_URL}/chat/gen_inspiration`,
    req
  )
  return res.data
}

type UserChattedListPayload = UserChattedListRes | ChattedCharacterListRes[]
type UserChattedListKeyReq = Omit<UserChattedListReq, 'page' | 'need_total'> & {
  need_total?: boolean
}

const normalizeChattedListItem = (item: ChattedCharacterListRes) => {
  return item.item_type === 'game' && item.game_id
    ? { ...item, item_id: item.game_id }
    : item
}

const normalizeUserChattedListRes = (
  data: UserChattedListPayload | undefined,
  req: UserChattedListReq
): UserChattedListRes => {
  if (Array.isArray(data)) {
    return {
      list: data.map(normalizeChattedListItem),
      total: data.length,
      has_more: data.length >= req.size,
    }
  }

  const list = data?.list || []
  const total = data?.total ?? list.length
  const currentPage = Math.max(0, req.page || 0)

  return {
    list: list.map(normalizeChattedListItem),
    total,
    has_more: data?.has_more ?? (currentPage + 1) * req.size < total,
  }
}

export const getUserChattedList = async (req: UserChattedListReq) => {
  const request = { ...req, need_total: true }
  const res = await axiosAuth.post<AppResp<UserChattedListPayload>>(
    `${BASE_API_URL}/user/chatted/list`,
    request
  )

  return normalizeUserChattedListRes(res.data.data, request)
}

//创建并从该消息开始新聊天
export const saveAndRestartChatCreateUrl = `${BASE_API_URL}/conversation/branch`
export const saveAndRestartChatCreate = async (
  character_id: string,
  branch_from_sequence: string,
  conversation_name: string
) => {
  const req: StartFromAMessageReq = {
    character_id,
    branch_from_sequence,
    conversation_name,
  }
  const res = await axiosAuth.post<
    AppResp<SaveAndRestartConversationCreateRes>
  >(saveAndRestartChatCreateUrl, req)
  if (!res.data.data) {
    throw new Error('handleCreateStartFromMessage error: empty response')
  }
  return res.data.data
}
//获取储存的开启新聊天列表
export const saveAndRestartChatGetListUrl = `${BASE_API_URL}/conversation/list`
export const saveAndRestartChatGetList = async (character_id: string) => {
  const res = await axiosAuth.post<
    AppResp<{
      active_conversation: SaveAndRestartConversation
      saved_conversations: SaveAndRestartConversation[]
    }>
  >(saveAndRestartChatGetListUrl, { character_id })

  if (!res.data.data) {
    throw new Error('handleGetStartFromMessageList error: empty response')
  }

  return res.data.data
}
//切换到已保存的开启新聊天
export const saveAndRestartChatSwitchUrl = `${BASE_API_URL}/conversation/switch`
export const saveAndRestartChatSwitch = async (conversation_id: string) => {
  const res = await axiosAuth.post<AppResp<SaveAndRestartConversation>>(
    saveAndRestartChatSwitchUrl,
    { conversation_id }
  )

  if (!res.data.data) {
    throw new Error('handleSwitchStartFromMessage error: empty response')
  }

  return res.data.data
}
//重命名已保存的开启新聊天
export const saveAndRestartChatRenameUrl = `${BASE_API_URL}/conversation/rename`
export const saveAndRestartChatRename = async (
  conversation_id: string,
  conversation_name: string
) => {
  const res = await axiosAuth.post<AppResp<{ message: string }>>(
    saveAndRestartChatRenameUrl,
    { conversation_id, conversation_name }
  )

  if (!res.data.data || res.data.data.message !== 'success') {
    throw new Error('saveAndRestartChat rename error: empty response')
  }

  return res.data.data
}
//删除已保存的开启新聊天
export const saveAndRestartChatDeleteUrl = `${BASE_API_URL}/conversation/delete`
export const saveAndRestartChatDelete = async (conversation_id: string) => {
  const res = await axiosAuth.post<
    AppResp<{
      deleted_conversation_id: string
      new_active_conversation_id: string //若删除的为当前活跃会话，则会选择剩下的最新一条会话作为新的活跃会话
    }>
  >(saveAndRestartChatDeleteUrl, { conversation_id })

  if (!res.data.data) {
    throw new Error('handleDeleteStartFromMessage error: empty response')
  }

  return res.data.data
}

export const getUserChattedListKey =
  (req: UserChattedListKeyReq) =>
  (page: number, previousPageData: UserChattedListRes | null) => {
    if (previousPageData && !previousPageData.has_more) return null
    return {
      endpoint: '/user/chatted/list',
      req: { ...req, page, need_total: true },
    }
  }

export const deleteUserChattedCharacter = async (
  req: DeleteUserChattedCharacterReq
) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/chatted/character/delete`,
    req
  )

  return res.data.data
}

export const deleteUserChattedStory = async (req: { story_id: string }) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/chatted/story/delete`,
    req
  )

  return res.data.data
}

export const deleteUserChattedGame = async (req: { game_id: string }) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/chatted/game/delete`,
    req
  )

  return res.data.data
}

export const editStoryMessage = async (req: EditStoryReplyReq) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/chat/story/edit_reply`,
    req
  )

  if (!res.data.data) throw new Error('Edit message failed')

  return res.data.data
}

export const editChatReply = async (req: EditChatReplyReq) => {
  const res = await axiosAuth.post<AppResp<boolean>>(
    `${BASE_API_URL}/chat/edit_reply`,
    req
  )

  return res.data.data
}

// 聊天置顶
export const updateChatPinned = async ({
  item_id,
  item_type,
  game_id,
}: {
  item_id: string
  item_type: 'character' | 'story' | 'game'
  game_id?: string
}) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/chatted/pin`,
    item_type === 'game'
      ? { item_type, game_id: game_id || item_id }
      : { item_id, item_type }
  )
  return res.data.data
}

// 聊天取消置顶
export const updateChatUnpinned = async ({
  item_id,
  item_type,
  game_id,
}: {
  item_id: string
  item_type: 'character' | 'story' | 'game'
  game_id?: string
}) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/chatted/unpin`,
    item_type === 'game'
      ? { item_type, game_id: game_id || item_id }
      : { item_id, item_type }
  )
  return res.data.data
}

export const shareMsgStats = async (
  item_id: string,
  item_type: 'character' | 'story',
  shared_msg_sequences: number[],
  channel: string
) => {
  const req = {
    item_id,
    item_type,
    shared_msg_sequences,
    channel,
  }

  const res = await axiosAuth.post<AppResp<null>>(
    `${BASE_API_URL}/chat/share`,
    req
  )

  return res.data
}
